import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Plot from "react-plotly.js";
import "../styles/PagesModern.css";
import { useNavigate } from "react-router-dom";
import { getIconFileName } from "../utils/iconUtils";
import { seriesList } from "../utils/seriesList";
import { useSeriesCategories } from "../hooks/useSeriesCategories";
import { carColors } from "../utils/carColors";
import SeriesPopularityHeatmap from "./SeriesPopularityHeatmap";
import CategoryIcon from "./CategoryIcon";
import { LoadingPanel, PageHeader, PanelHeader, SegmentedControl } from "./RefreshUI";
import { CHART, chartAxis, chartBase, chartLegend, chartWatermark, panelChartWidth } from "../utils/chartTheme";
import { CATEGORIES, CATEGORY_LABELS } from "../utils/racingLabels";

function SeriesLapTimeChart() {
        const { season_name } = useParams();
        const [data, setData] = useState([]);
        const [loading, setLoading] = useState(true);
        const [searchTerm, setSearchTerm] = useState("");
        const [selectedCategory, setSelectedCategory] = useState("");
        const [weekNumber, setWeekNumber] = useState(null);
        const [weekNumberSelected, setWeekNumberSelected] = useState(null);
        const [filteredSeriesList, setFilteredSeriesList] = useState(seriesList);
        const [isQualifying, setIsQualifying] = useState(true);
        const [trackNames, setTrackNames] = useState([]);
        const [showTableSelections, setShowTableSelections] = useState(false);
        const [multiclassHeightModifier, setMulticlassHeightModifier] = useState(0);
        const seriesCategoryMap = useSeriesCategories();
        let minY = isQualifying ? 0 : 60;
        const maxY = data.length > 0 ? Math.max(...data.flatMap((item) => item.best_lap_time_avg / 10000)) : 0;

        useEffect(() => {
                setLoading(true);
                async function fetchWeekNumber() {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/week-number");
                                const json = await response.json();

                                // If `json` is a string, convert it here:
                                const numericWeek = parseInt(json, 10);

                                setWeekNumber(numericWeek);
                                setWeekNumberSelected(numericWeek); // Set selected week number to current week
                                setLoading(false);
                        } catch (error) {
                                console.error("Error fetching week number:", error);
                        }
                }

                fetchWeekNumber();
        }, []);

        useEffect(() => {
                setLoading(true);
                async function fetchSeasonTrackNames() {
                        try {
                                const response = await fetch(`https://iracing6-backend.herokuapp.com/api/series-basic-info/series-basic-info/seasonTracks/${season_name}`);
                                const json = await response.json();

                                const trackNames = json;
                                setTrackNames(trackNames);
                                setLoading(false);
                        } catch (error) {
                                console.error("Error fetching season track names:", error);
                        }
                }

                fetchSeasonTrackNames();
        }, [season_name]);

        useEffect(() => {
                let filtered = seriesList;

                // Filter by category using API data
                if (selectedCategory && Object.keys(seriesCategoryMap).length > 0) {
                        filtered = filtered.filter((series) => seriesCategoryMap[series] === selectedCategory);
                }

                // Filter by search term
                filtered = filtered.filter((series) => series.toLowerCase().includes(searchTerm.toLowerCase()));

                setFilteredSeriesList(filtered);
        }, [searchTerm, selectedCategory, seriesCategoryMap]);

        const groupedData = data.reduce((acc, item) => {
                const className = item.compositeKey.carClassName;
                if (!acc[className]) {
                        acc[className] = [];
                }
                acc[className].push(item);
                return acc;
        }, {});

        useEffect(() => {
                if (weekNumberSelected !== null) {
                        setLoading(true);
                        async function fetchData() {
                                try {
                                        let endpoint;

                                        if (weekNumberSelected === weekNumber) {
                                                endpoint = isQualifying
                                                        ? `https://iracing6-backend.herokuapp.com/api/series-avg-lap-times/average-lap-time/${season_name}`
                                                        : `https://iracing6-backend.herokuapp.com/api/series-avg-lap-times/average-race-lap-time/${season_name}`;
                                        } else {
                                                endpoint = isQualifying
                                                        ? `https://iracing6-backend.herokuapp.com/api/series-avg-lap-times/average-lap-time-historic/${season_name}/${weekNumberSelected}`
                                                        : `https://iracing6-backend.herokuapp.com/api/series-avg-lap-times/average-race-lap-time-historic/${season_name}/${weekNumberSelected}`;
                                        }

                                        const response = await fetch(endpoint);

                                        // If we get a 404 or other error status, and we're in qualifying mode, try race mode
                                        if (!response.ok && isQualifying) {
                                                setIsQualifying(false);
                                                return; // Let the effect re-run with race mode
                                        }

                                        const json = await response.json();

                                        // If qualifying data is empty or not an array, and we're in qualifying mode, switch to race mode
                                        if ((!Array.isArray(json) || json.length === 0) && isQualifying) {
                                                setIsQualifying(false);
                                                // Keep loading true so spinner shows while race data loads
                                                return; // Let the effect re-run with race mode
                                        }

                                        const lapTimes = json.map((item) => item.best_lap_time_avg / 10000);
                                        const Q1 = lapTimes.sort((a, b) => a - b)[Math.floor(lapTimes.length / 4)];
                                        const Q3 = lapTimes.sort((a, b) => a - b)[Math.floor((3 * lapTimes.length) / 4)];
                                        const IQR = Q3 - Q1;
                                        const lowerBound = Q1 - 2 * IQR;
                                        const upperBound = Q3 + 2 * IQR;

                                        const filteredData = json.filter((item) => {
                                                const lapTime = item.best_lap_time_avg / 10000;
                                                return lapTime >= lowerBound && lapTime <= upperBound;
                                        });

                                        const sortedData = filteredData.sort((a, b) => a.i_rating_range[0] - b.i_rating_range[0]);

                                        setData(sortedData);
                                        setLoading(false);
                                } catch (error) {
                                        console.error("Error fetching data:", error);
                                        setLoading(false);
                                }
                        }

                        fetchData();
                }
        }, [season_name, isQualifying, weekNumberSelected]);

        useEffect(() => {
                if (Object.keys(groupedData).length > 0) {
                        const classStats = Object.keys(groupedData).map((className) => {
                                const classData = groupedData[className];
                                const lapTimes = classData.map((item) => item.best_lap_time_avg / 10000);
                                const sortedLapTimes = lapTimes.slice().sort((a, b) => a - b);
                                const mid = Math.floor(sortedLapTimes.length / 2);
                                const median = sortedLapTimes.length % 2 !== 0 ? sortedLapTimes[mid] : (sortedLapTimes[mid - 1] + sortedLapTimes[mid]) / 2;
                                return {
                                        className,
                                        lapTimes,
                                        median,
                                };
                        });

                        classStats.sort((a, b) => b.median - a.median);

                        if (classStats.length > 15) {
                                setMulticlassHeightModifier(200); // Update the state
                        } else {
                                setMulticlassHeightModifier(0); // Reset if condition is not met
                        }
                }
        }, [groupedData]); // Only run when groupedData changes

        const navigate = useNavigate();

        const handleLinkToSeriesPop = () => {
                navigate("/charts/SeriesPopularity");
        };

        const getIconUrl = (seasonName) => {
                const iconName = getIconFileName(seasonName);
                return `${process.env.PUBLIC_URL}/assets/series-icons/${iconName}.png`;
        };

        const handleLinkToSpecificSeries = (seriesName) => {
                const index = seriesList.indexOf(seriesName);
                const encodedSeriesName = encodeURIComponent(seriesList[index]);
                navigate(`/charts/SeriesLapTimeChart/${encodedSeriesName}`);
        };

        // Drop only the "2026 Season 4" part, keeping suffixes like "- Fixed" that tell series apart.
        const displaySeriesName = (name) =>
                name
                        .replace(/\s*-?\s*\d{4} Season(\s+\d+)?/, "")
                        .replace(/^\s*-\s*|\s*-\s*$/g, "")
                        .trim();
        const seasonLabel = season_name ? (season_name.match(/\d{4} Season \d+/) || [""])[0] : "";
        const sessionLabel = isQualifying ? "qualifying" : "race";

        const seriesControls = (
                <>
                        <SegmentedControl
                                label="Session"
                                value={isQualifying ? "q" : "r"}
                                onChange={(value) => setIsQualifying(value === "q")}
                                options={[
                                        { value: "q", content: "Qualifying" },
                                        { value: "r", content: "Race" },
                                ]}
                        />
                        <label className="pg-field__row">
                                Week
                                <select className="pg-input" value={weekNumberSelected || ""} onChange={(e) => setWeekNumberSelected(parseInt(e.target.value))}>
                                        {Array.from({ length: weekNumber || 0 }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                        {i + 1}
                                                </option>
                                        ))}
                                </select>
                        </label>
                </>
        );

        const seriesHeader = season_name && (
                <PageHeader
                        title={displaySeriesName(season_name)}
                        lede={`${seasonLabel ? `${seasonLabel}, ` : ""}week ${weekNumberSelected ?? "–"} of 13${trackNames[weekNumberSelected - 1] ? ` at ${trackNames[weekNumberSelected - 1]}` : ""}.`}
                >
                        {seriesControls}
                </PageHeader>
        );

        if (loading || weekNumberSelected === null) {
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        {seriesHeader || <PageHeader title="Series lap times" />}
                                        <LoadingPanel />
                                </main>
                        </div>
                );
        }

        if (season_name && data.length === 0) {
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        {seriesHeader}
                                        <section className="hp-panel pg-loading">
                                                <p className="hp-panel__note">
                                                        No {sessionLabel} lap times for this series in week {weekNumberSelected} yet. Try another week or session.
                                                </p>
                                        </section>
                                </main>
                        </div>
                );
        }

        const plotData = () => {
                const allXValues = data.flatMap((item) => item.i_rating_range);
                const minX = Math.min(...allXValues);
                const maxX = Math.max(...allXValues);
                const xValues = [];
                for (let x = minX; x <= maxX; x += 100) {
                        xValues.push(x);
                }

                const plotLines = Object.keys(groupedData).map((className) => {
                        const classData = groupedData[className];
                        const yValues = xValues.map((x) => {
                                const item = classData.find((item) => x >= item.i_rating_range[0] && x <= item.i_rating_range[1]);
                                return item ? item.best_lap_time_avg / 10000 : null;
                        });

                        // Filter out leading and trailing null values to prevent gaps at the ends
                        const firstNonNullIndex = yValues.findIndex((value) => value !== null);
                        const lastNonNullIndex = yValues.length - 1 - [...yValues].reverse().findIndex((value) => value !== null);
                        const trimmedXValues = xValues.slice(firstNonNullIndex, lastNonNullIndex + 1);
                        const trimmedYValues = yValues.slice(firstNonNullIndex, lastNonNullIndex + 1);

                        return {
                                x: trimmedXValues,
                                y: trimmedYValues,
                                type: "scatter",
                                mode: "lines",
                                line: { width: 2, shape: "spline", smoothing: 0.6 },
                                marker: { color: carColors[className] || "#f1c40f" },
                                name: className,
                                connectgaps: true,
                                // fill: "tonexty", // Fill area below the line
                                fillcolor: `rgba(241, 196, 15, 0.2)`, // Gradient color (adjust opacity as needed)
                        };
                });

                // Check if there is only one line being plotted
                if (plotLines.length === 1) {
                        plotLines[0].line = { ...plotLines[0].line, color: "#f1c40f" };
                }

                return plotLines;
        };

        const boxPlotData = () => {
                const classStats = Object.keys(groupedData).map((className) => {
                        const classData = groupedData[className];
                        const lapTimes = classData.map((item) => item.best_lap_time_avg / 10000);
                        const sortedLapTimes = lapTimes.slice().sort((a, b) => a - b);
                        const mid = Math.floor(sortedLapTimes.length / 2);
                        const median = sortedLapTimes.length % 2 !== 0 ? sortedLapTimes[mid] : (sortedLapTimes[mid - 1] + sortedLapTimes[mid]) / 2;
                        return {
                                className,
                                lapTimes,
                                median,
                        };
                });

                classStats.sort((a, b) => b.median - a.median);

                return classStats.map(({ className, lapTimes }) => ({
                        x: lapTimes, // Keep as numerical values (seconds)
                        type: "box",
                        name: className,
                        marker: { color: carColors[className] || "#f1c40f" },
                        boxpoints: false,
                        boxWidth: 0.5,
                        orientation: "h",
                }));
        };

        const formatTime = (seconds) => {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = Math.floor(seconds % 60); // Remove milliseconds
                return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`; // Ensures MM:SS format
        };

        const formatTimeWithDecimals = (seconds) => {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = (seconds % 60).toFixed(2); // Keep 2 decimal places
                return `${minutes}:${remainingSeconds.padStart(5, "0")}`; // Ensures MM:SS.ss format
        };

        const yMaxCeil = Math.ceil(maxY);
        const actualMinY = data.length > 0 ? Math.min(...data.flatMap((item) => item.best_lap_time_avg / 10000)) : 0;
        const yMinFloor = Math.floor(actualMinY);
        const yRangeCountIfStep1 = yMaxCeil - yMinFloor + 1;
        const yTickStep = yRangeCountIfStep1 > 30 ? 2 : 1; // Use 2s only if >30 labels at 1s
        const yTickVals = [];
        for (let v = yMinFloor; v <= yMaxCeil; v += yTickStep) {
                yTickVals.push(v);
        }
        const yTickText = yTickVals.map(formatTime);

        const isWide = window.innerWidth > 800;
        const figWidth = panelChartWidth();
        const yTitle = isQualifying ? "Avg qualifying lap" : "Avg best race lap";

        const layout = {
                ...chartBase(),
                margin: isWide ? { l: 72, r: 16, t: 8, b: 90 } : { l: 44, r: 6, t: 22, b: 44 },
                xaxis: {
                        ...chartAxis(),
                        title: { text: "iRating", font: { size: 12, color: CHART.muted }, standoff: 8 },
                        tick0: 0,
                        dtick: 500,
                        showgrid: false,
                },
                yaxis: {
                        ...chartAxis(),
                        title: { text: yTitle, font: { size: 12, color: CHART.muted } },
                        tickvals: yTickVals,
                        ticktext: yTickText,
                        ticklen: 0,
                        automargin: true,
                },
                hovermode: false,
                legend: { ...chartLegend({ x: 0.5, y: -0.16, xanchor: "center", yanchor: "top" }), orientation: "h", bgcolor: "rgba(0,0,0,0)", borderwidth: 0 },
                showlegend: isWide,
                // On phones the lines fill the plot, so the watermark sits in the (taller) top margin.
                annotations: [isWide ? chartWatermark("top-right") : chartWatermark("top-right", { outside: true })],
                width: figWidth,
                height: isWide ? 560 + multiclassHeightModifier : 400 + multiclassHeightModifier / 2,
        };

        const mergedLayout = {
                ...chartBase(),
                yaxis: { ...chartAxis(), showticklabels: false, showgrid: false },
                xaxis: {
                        ...chartAxis(),
                        title: { text: yTitle, font: { size: 12, color: CHART.muted }, standoff: 8 },
                        tickvals: yTickVals, // Numerical x values
                        ticktext: yTickVals.map(formatTime), // Formatted labels
                },
                hovermode: false,
                legend: {
                        ...chartLegend({ x: 0, y: isWide ? -0.22 : -0.3, xanchor: "left", yanchor: "top" }),
                        orientation: "h",
                        bgcolor: "rgba(0,0,0,0)",
                        borderwidth: 0,
                        traceorder: "reversed",
                },
                width: figWidth,
                height: isWide ? 400 + multiclassHeightModifier : 300 + multiclassHeightModifier / 2,
                annotations: [chartWatermark("top-right")],
                margin: isWide ? { l: 16, r: 16, t: 8, b: 110 } : { l: 8, r: 8, t: 8, b: 90 },
                autosize: false,
        };

        if (!season_name) {
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        <PageHeader title="Series lap times" lede="Average lap times by iRating for each series. Pick a series below, or browse them by popularity.">
                                                <button type="button" className="hp-link" onClick={handleLinkToSeriesPop}>
                                                        Browse by popularity
                                                </button>
                                        </PageHeader>
                                        <section className="hp-panel">
                                                <div className="pg-filters">
                                                        <div className="pg-field">
                                                                <span className="pg-field__label">Category</span>
                                                                <SegmentedControl
                                                                        label="Category"
                                                                        value={selectedCategory}
                                                                        onChange={setSelectedCategory}
                                                                        options={[
                                                                                { value: "", title: "All categories", content: "All" },
                                                                                ...CATEGORIES.map((cat) => ({ value: cat, title: CATEGORY_LABELS[cat], content: <CategoryIcon category={cat} size={16} /> })),
                                                                        ]}
                                                                />
                                                        </div>
                                                        <div className="pg-field">
                                                                <label className="pg-field__label" htmlFor="slt-search">
                                                                        Search
                                                                </label>
                                                                <input id="slt-search" type="text" className="pg-input pg-input--wide" placeholder="Series name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                                        </div>
                                                </div>
                                        </section>
                                        <section className="hp-panel">
                                                <PanelHeader title={`${filteredSeriesList.length} series`} />
                                                <div className="pg-series-grid">
                                                        {filteredSeriesList.map((series) => (
                                                                <button key={series} type="button" className="pg-series-tile" title={series} onClick={() => handleLinkToSpecificSeries(series)}>
                                                                        <img src={getIconUrl(series)} alt="" />
                                                                        <span>{displaySeriesName(series)}</span>
                                                                </button>
                                                        ))}
                                                </div>
                                        </section>
                                </main>
                        </div>
                );
        }

        const uniqueCarClasses = Object.keys(groupedData);
        const boxPlot = boxPlotData();

        return (
                <div className="pg">
                        <main className="hp-main">
                                {seriesHeader}

                                <section className="hp-panel">
                                        <PanelHeader title={`${isQualifying ? "Qualifying" : "Race"} pace by iRating`} />
                                        <p className="hp-panel__note">
                                                Average {isQualifying ? "qualifying lap" : "best race lap"} for drivers in each 100-point iRating band
                                                {uniqueCarClasses.length > 1 ? ", one line per car class." : "."}
                                        </p>
                                        <div className="hp-chart">
                                                <Plot data={plotData()} layout={layout} config={{ displayModeBar: false }} />
                                        </div>
                                </section>

                                {uniqueCarClasses.length > 1 && (
                                        <section className="hp-panel">
                                                <PanelHeader title="Lap time spread by class" />
                                                <p className="hp-panel__note">Each box covers the middle half of a car class's lap times. Further left is faster.</p>
                                                <div className="hp-chart">
                                                        <Plot data={boxPlot} layout={mergedLayout} config={{ displayModeBar: false }} />
                                                </div>
                                        </section>
                                )}

                                <SeriesPopularityHeatmap seasonName={season_name} />

                                <section className="hp-panel">
                                        <PanelHeader
                                                title="Lap time tables"
                                                action={
                                                        <button type="button" className="hp-link" onClick={() => setShowTableSelections(!showTableSelections)}>
                                                                {showTableSelections ? "Hide tables" : "Show tables"}
                                                        </button>
                                                }
                                        />
                                        {showTableSelections ? (
                                                <div className="pg-class-tables">
                                                        {uniqueCarClasses.map((className) => (
                                                                <div key={className}>
                                                                        <h3 className="pg-class-table__title">
                                                                                <span className="pg-swatch" style={{ "--pg-swatch": carColors[className] || CHART.yellow }} />
                                                                                {className}
                                                                        </h3>
                                                                        <table className="pg-table">
                                                                                <thead>
                                                                                        <tr>
                                                                                                <th>iRating</th>
                                                                                                <th className="pg-num">{yTitle}</th>
                                                                                        </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                        {groupedData[className].map((item) => (
                                                                                                <tr key={item.i_rating_range.join("-")}>
                                                                                                        <td>{item.i_rating_range.join("–")}</td>
                                                                                                        <td className="pg-num">{formatTimeWithDecimals(item.best_lap_time_avg / 10000)}</td>
                                                                                                </tr>
                                                                                        ))}
                                                                                </tbody>
                                                                        </table>
                                                                </div>
                                                        ))}
                                                </div>
                                        ) : (
                                                <p className="hp-panel__note">Exact average lap times for each iRating band, per car class.</p>
                                        )}
                                </section>
                        </main>
                </div>
        );
}

export default SeriesLapTimeChart;
